import { useEffect, useRef, useCallback } from 'react';
import { fetchSessionInfo } from '../api/fetchSessionInfo';
import { captureException, toError } from '../../../../internal/ravenUtils';
const POLLING_INTERVAL_MS = 1000;
export const MAX_POLLING_COUNT = 300;
export function useSessionPolling({
  portalId,
  sessionId,
  callbacks,
  enabled
}) {
  const pollingTimeoutRef = useRef(undefined);
  const pollingCountRef = useRef(0);
  const isPollingRef = useRef(false);
  const callbacksRef = useRef(callbacks);
  useEffect(() => {
    callbacksRef.current = callbacks;
  }, [callbacks]);
  const stopPolling = useCallback(() => {
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = undefined;
    }
    isPollingRef.current = false;
  }, []);
  const pollSessionInfo = useCallback(() => {
    if (!isPollingRef.current) {
      return;
    }
    fetchSessionInfo(portalId, sessionId).then(response => {
      if (!isPollingRef.current) {
        return;
      }
      pollingCountRef.current += 1;
      callbacksRef.current.onPollTick();
      if (response.status === 'PENDING') {
        if (pollingCountRef.current >= MAX_POLLING_COUNT) {
          stopPolling();
          callbacksRef.current.onTimeout();
        } else {
          pollingTimeoutRef.current = window.setTimeout(pollSessionInfo, POLLING_INTERVAL_MS);
        }
      } else {
        stopPolling();
        callbacksRef.current.onPollSuccess(response.params, response.status);
      }
    }).catch(err => {
      // An in-flight poll can reject after polling stopped, e.g. when the
      // popup's window-message path already completed the flow. That is not
      // a failure, so bail before reporting.
      if (!isPollingRef.current) {
        return;
      }
      captureException(new Error('Auth session polling request failed', {
        cause: toError(err)
      }));
      stopPolling();
      callbacksRef.current.onPollError();
    });
  }, [portalId, sessionId, stopPolling]);
  const startPolling = useCallback(() => {
    if (isPollingRef.current) {
      return;
    }
    pollingCountRef.current = 0;
    isPollingRef.current = true;
    pollSessionInfo();
  }, [pollSessionInfo]);
  useEffect(() => {
    if (enabled && !isPollingRef.current) {
      startPolling();
    } else if (!enabled) {
      stopPolling();
    }
  }, [enabled, startPolling, stopPolling]);
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);
  return {
    startPolling,
    stopPolling,
    isPolling: isPollingRef.current,
    pollingCount: pollingCountRef.current
  };
}